import { type Code, ConnectError } from "@connectrpc/connect";
import { captureException } from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { AppError, type ERROR_CODES } from "./errors";

type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

type QueryErrorMeta = {
  // Code used for the AppError sent to Sentry and shown on ErrorPage.
  errorCode: ErrorCode;
  // ConnectError codes treated as expected: not reported to Sentry, not thrown to the boundary.
  expectedConnectCodes?: readonly Code[];
};

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: QueryErrorMeta;
    mutationMeta: QueryErrorMeta;
  }
}

export function isExpectedConnectError(
  error: unknown,
  meta: Pick<QueryErrorMeta, "expectedConnectCodes"> | undefined,
): boolean {
  return (
    error instanceof ConnectError &&
    !!meta?.expectedConnectCodes?.includes(error.code)
  );
}

function reportQueryError(error: unknown, meta: QueryErrorMeta | undefined) {
  if (!meta) return;
  if (isExpectedConnectError(error, meta)) return;
  const appError = new AppError(
    meta.errorCode,
    error instanceof Error ? error.message : "Unknown error",
    { originalError: error },
  );
  if (appError.skipSentry) return;
  console.error(appError);
  captureException(appError);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // See https://tkdodo.eu/blog/react-query-error-handling#throwonerror
        throwOnError: (error, query) => {
          if (!query.meta) return false;
          return !isExpectedConnectError(error, query.meta);
        },
        // Mutations can't be handled here (throwOnError has no mutation access); wire per useMutation.
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        reportQueryError(error, query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        reportQueryError(error, mutation.meta);
      },
    }),
  });
}

// アプリケーション全体で使用する単一のQueryClientインスタンス
let queryClient: QueryClient | undefined;

/**
 * グローバルなQueryClientインスタンスを取得します
 * main.tsxで初期化されたクライアントがない場合は新しいインスタンスを作成します
 */
export function getQueryClient(): QueryClient {
  if (!queryClient) {
    // バックグラウンドでクエリクライアントが初期化されていない場合（SSRなど）
    queryClient = createQueryClient();
  }
  return queryClient;
}

/**
 * QueryClientインスタンスを設定します
 * 通常はmain.tsxで初期化時に呼び出します
 */
export function setQueryClient(client: QueryClient): void {
  queryClient = client;
}
